import { isUndefined } from 'util';
import { Line } from '../line';
import { MetaData } from '../MetaData';
import { DISRUPTION_TYPE, DISRUPTION_PRIORITY } from '../../enums/model.enums';
import { TrafficNode } from '../trafficNodes/trafficNode';
import { Provider } from '../mots/provider';
export class Disruption extends MetaData {
    /**
     * constructor of class
     * please use factory instead
     * @see {this.factory)
     * @param {string} id
     * @param {string[]} content
     * @param {string} url
     * @param {Line[]} lines
     * @param {start: Date,	end: Date} validityPeriod
     */
    constructor(id, content, headers, url, lines, validityPeriod, publicationPeriod, provider, additionalInformation, type, nodes, tripRequestIncluded, priority, metaData) {
        super(metaData);
        this._id = id;
        this._content = content;
        this._url = url;
        this._headers = headers;
        this._lines = lines;
        this._nodes = nodes;
        this._validityPeriod = validityPeriod;
        this._publicationPeriod = publicationPeriod;
        this._provider = provider;
        this._additionalInformation = additionalInformation;
        this._type = type;
        this._tripRequestIncluded = tripRequestIncluded;
        this._priority = priority;
    }
    /**
     * Factory method to create an instance of this class without
     * having to correctly fill the constructor.
     * !!! You should always use this instead of the constructor !!!
     * @param values
     * @returns {Disruption}
     */
    static factory(values) {
        const sanitizedValues = {};
        sanitizedValues.id = values.id ? values.id : '';
        sanitizedValues.content = values.content ? values.content : [];
        sanitizedValues.headers = values.headers ? values.headers : [];
        sanitizedValues.url = values.url ? values.url : '';
        sanitizedValues.lines = [];
        if (values.lines) {
            for (const line of values.lines) {
                sanitizedValues.lines.push(Line.factory(line));
            }
        }
        sanitizedValues.validityPeriod = [];
        if (!isUndefined(values.validityPeriod)) {
            for (const validityPeriod of values.validityPeriod) {
                sanitizedValues.validityPeriod.push(validityPeriod.end
                    ? {
                        start: new Date(validityPeriod.start),
                        end: new Date(validityPeriod.end)
                    }
                    : {
                        start: new Date(validityPeriod.start),
                        end: undefined
                    });
            }
        }
        const publicationPeriod = {
            start: new Date(),
            end: undefined
        };
        if (values.publicationPeriod !== undefined) {
            if (values.publicationPeriod.end !== undefined) {
                publicationPeriod.start = new Date(values.publicationPeriod.start);
                publicationPeriod.end = new Date(values.publicationPeriod.end);
            }
            else {
                publicationPeriod.start = new Date(values.publicationPeriod.start);
            }
        }
        sanitizedValues.publicationPeriod = publicationPeriod;
        sanitizedValues.provider = !isUndefined(values.provider) ? Provider.factory(values.provider) : Provider.factory({});
        sanitizedValues.additionalInformation = values.additionalInformation ? values.additionalInformation : [];
        sanitizedValues.type = !isUndefined(values.type) ? values.type : DISRUPTION_TYPE.DISRUPTION;
        sanitizedValues.nodes = [];
        if (values.nodes) {
            for (const node of values.nodes) {
                sanitizedValues.nodes.push(TrafficNode.factory(node));
            }
        }
        sanitizedValues.tripRequestIncluded = values.tripRequestIncluded;
        sanitizedValues.priority = values.priority !== undefined ? values.priority : DISRUPTION_PRIORITY.NORMAL;
        sanitizedValues.metaData = !isUndefined(values.metaData) ? values.metaData : {};
        return new Disruption(sanitizedValues.id, sanitizedValues.content, sanitizedValues.headers, sanitizedValues.url, sanitizedValues.lines, sanitizedValues.validityPeriod, sanitizedValues.publicationPeriod, sanitizedValues.provider, sanitizedValues.additionalInformation, sanitizedValues.type, sanitizedValues.nodes, sanitizedValues.tripRequestIncluded, sanitizedValues.priority, sanitizedValues.metaData);
    }
    /**
     * getter for id
     * @returns {string}
     */
    get id() {
        return this._id;
    }
    /**
     * setter for id
     * @param {string} value
     */
    set id(value) {
        this._id = value;
    }
    /**
     * Getter for the explaining texts about this disruption
     */
    get content() {
        return this._content;
    }
    /**
     * Setter for the explaining texts about this disruption
     */
    set content(value) {
        this._content = value;
    }
    /**
     * Getter for the heading lines of this disruption
     */
    get headers() {
        return this._headers;
    }
    /**
     * Setter for the heading lines of this disruption
     */
    set headers(value) {
        this._headers = value;
    }
    /**
     * Getter the URL linking to the details page for this disruption.
     * Can be used to link the user to a external page containing more
     * information
     */
    get url() {
        return this._url;
    }
    /**
     * Setter the URL linking to the details page for this disruption.
     * Can be used to link the user to a external page containing more
     * information
     */
    set url(value) {
        this._url = value;
    }
    /**
     * Getter for Line objects for the lines that are influenced by this
     * disruption without duplicates
     */
    get uniqueLines() {
        const uniqueLines = [];
        for (const line of this._lines) {
            if (!uniqueLines.some((i) => {
                return i.lineNumber === line.lineNumber;
            })) {
                uniqueLines.push(line);
            }
        }
        return uniqueLines;
    }
    /**
     * Getter for Line objects for the lines that are influenced by this
     * disruption
     */
    get lines() {
        return this._lines;
    }
    /**
     * Setter for Line objects for the lines that are influenced by this
     * disruption
     */
    set lines(value) {
        this._lines = value;
    }
    /**
     * Getter for the array of objects containing the start and end
     * time of the validity period of this disruption
     */
    get validityPeriod() {
        return this._validityPeriod;
    }
    /**
     * Setter for the array of objects containing the start and end
     * time of the validity period of this disruption
     * @param value Object containing the times
     */
    set validityPeriod(value) {
        this._validityPeriod = value;
    }
    /**
     * Getter for the array of objects containing the start and end
     * time of the period in which the disruption should be presented to the user
     */
    get publicationPeriod() {
        return this._publicationPeriod;
    }
    /**
     * Setter for the array of objects containing the start and end
     * time of the period in which the disruption should be presented to the user
     * @param value Object containing the times
     */
    set publicationPeriod(value) {
        this._publicationPeriod = value;
    }
    /**
     * Getter for Provider information on the disruption
     */
    get provider() {
        return this._provider;
    }
    /**
     * Setter for Provider information on the disruption
     */
    set provider(provider) {
        this._provider = provider;
    }
    /**
     * Getter for additional information on the disruption
     */
    get additionalInformation() {
        return this._additionalInformation;
    }
    /**
     * Setter for additional information on the disruption
     */
    set additionalInformation(value) {
        this._additionalInformation = value;
    }
    /**
     * Getter for additional information on the disruption
     */
    get type() {
        return this._type;
    }
    /**
     * Setter for additional information on the disruption
     */
    set type(value) {
        this._type = value;
    }
    /**
     * Getter for TrafficNode objects for the stops that are influenced by this
     * disruption
     */
    get nodes() {
        return this._nodes;
    }
    /**
     * Setter for TrafficNode objects for the stops that are influenced by this
     * disruption
     */
    set nodes(value) {
        this._nodes = value;
    }
    /**
     * Getter for TripRequest consideration for this disruption
     */
    get tripRequestIncluded() {
        return this._tripRequestIncluded;
    }
    /**
     * Setter for TripRequest consideration for this disruption
     */
    set tripRequestIncluded(value) {
        this._tripRequestIncluded = value;
    }
    /**
     * Getter for priority for this disruption
     */
    get priority() {
        return this._priority;
    }
    /**
     * Setter for priority for this disruption
     */
    set priority(value) {
        this._priority = value;
    }
}
//# sourceMappingURL=disruption.js.map